'use client'

import Image from 'next/image'
import { Diamond, Compass, Award } from 'lucide-react'
import { motion } from 'motion/react'

const pillars = [
    {
        title: 'Craftsmanship',
        description: 'Every detail is meticulously crafted to perfection, ensuring a living experience that is both luxurious and enduring.',
        icon: Diamond,
    },
    // ... (rest of pillars same as existing)
    {
        title: 'Thoughtful Design',
        description: 'We blend aesthetics with functionality, creating spaces that inspire and elevate the standard of modern living.',
        icon: Compass,
    },
    {
        title: 'Signature Quality',
        description: 'Our commitment to quality is unwavering, setting benchmarks in the industry with superior materials and construction.',
        icon: Award,
    },
]

export function AboutVisionMission() {
    return (
        <section className="py-24 bg-[#FEFBF6]">
            <div className="container px-4 mx-auto">
                <div className="flex flex-col gap-24 mb-24">
                    {/* Vision Section - Text Left, Image Right */}
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="space-y-8 order-2 md:order-1"
                        >
                            <div className="space-y-4">
                                <span className="text-xs font-bold tracking-[0.3em] text-secondary uppercase block">
                                    Philosophy
                                </span>
                                <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[0.9] text-primary">
                                    Our <span className="italic text-primary/50">Vision</span>
                                </h2>
                            </div>
                            <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                                <p>
                                    Nova Realty aspires to become the most trusted real estate developer both regionally and globally. We aim to redefine the art of living by creating iconic landmarks that stand the test of time, blending architectural brilliance with sustainable innovation.
                                </p>
                                <p>
                                    We envision a future where every home is a masterpiece, designed not just for today but for generations to come. Our commitment goes beyond construction; it is about crafting vibrant communities where life flourishes in harmony with luxury and nature.
                                </p>
                            </div>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="relative aspect-[4/5] md:aspect-square overflow-hidden rounded-2xl order-1 md:order-2 shadow-2xl"
                        >
                            <Image
                                src="/images/about/vision.png"
                                alt="Our Vision"
                                fill
                                className="object-cover scale-150 hover:scale-[1.6] transition-transform duration-700"
                                sizes="(max-width: 768px) 100vw, 50vw"
                            />
                        </motion.div>
                    </div>

                    {/* Mission Section - Image Left, Text Right */}
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="relative aspect-[4/5] md:aspect-square overflow-hidden rounded-2xl order-1 shadow-2xl"
                        >
                            <Image
                                src="/images/about/mission.png"
                                alt="Our Mission"
                                fill
                                className="object-cover scale-150 hover:scale-[1.6] transition-transform duration-700"
                                sizes="(max-width: 768px) 100vw, 50vw"
                            />
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="space-y-8 order-2"
                        >
                            <div className="space-y-4">
                                <span className="text-xs font-bold tracking-[0.3em] text-secondary uppercase block">
                                    Purpose
                                </span>
                                <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[0.9] text-primary">
                                    Our <span className="italic text-primary/50">Mission</span>
                                </h2>
                            </div>
                            <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                                <p>
                                    By pioneering innovative global projects and transforming perceptions on quality, we work to challenge existing norms and standards that will ultimately enrich communities and societies at large.
                                </p>
                                <p>
                                    Our mission is anchored in an unwavering dedication to craftsmanship. We strive to deliver properties that are not merely structures, but sanctuaries of peace and prestige. Through transparent practices and client-centric service, we build relationships as strong as our foundations.
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </div>

                <div className="space-y-16">
                    <div className="text-center mb-16 space-y-4">
                        <span className="text-xs font-bold tracking-[0.3em] text-secondary uppercase block">
                            Core Values
                        </span>
                        <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[0.9] text-primary">
                            Built on <span className="italic text-primary/50">3 Key Pillars</span>
                        </h2>
                    </div>

                    {/* Staggered Grid Animation */}
                    <motion.div
                        className="grid md:grid-cols-3 gap-8"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                        variants={{
                            visible: { transition: { staggerChildren: 0.2 } }
                        }}
                    >
                        {pillars.map((pillar) => (
                            <motion.div
                                key={pillar.title}
                                variants={{
                                    hidden: { opacity: 0, y: 30 },
                                    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
                                }}
                                className="bg-white p-10 rounded-xl text-center shadow-xl hover:shadow-2xl transition-all duration-300 border border-primary/5"
                            >
                                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#FEFBF6] text-primary mb-8 border border-primary/10">
                                    <pillar.icon className="w-10 h-10" />
                                </div>
                                <h3 className="text-2xl font-serif text-primary mb-4">{pillar.title}</h3>
                                <p className="text-muted-foreground leading-relaxed">
                                    {pillar.description}
                                </p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </div>
        </section >
    )
}
